/**
 * DiagnosticsPanel - P1.5 Read-only system health checks
 * Displays findings by severity with actionable links
 */

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, AlertCircle, Info, CheckCircle, ExternalLink,
  RefreshCw, Loader2, Shield, ChevronDown, ChevronRight, Clock,
  Activity, XCircle
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Severity configuration
const SEVERITY_CONFIG = {
  critical: {
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    label: 'Critical'
  },
  high: {
    icon: AlertTriangle,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    label: 'High'
  },
  info: {
    icon: Info,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    label: 'Info'
  }
};

// Single finding card
const FindingCard = ({ finding, onValidatorRun }) => {
  const [expanded, setExpanded] = useState(false);
  const config = SEVERITY_CONFIG[finding.severity] || SEVERITY_CONFIG.info;
  const Icon = config.icon;
  
  return (
    <div className={`border ${config.border} rounded-lg ${config.bg} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-3 text-left"
      >
        <Icon className={`w-4 h-4 ${config.color} flex-shrink-0 mt-0.5`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-semibold uppercase ${config.color}`}>
              {config.label}
            </span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs text-gray-500 font-mono">{finding.code}</span>
          </div>
          <h4 className="text-sm font-medium text-gray-900 mt-0.5">{finding.title}</h4>
        </div>
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>
      
      {expanded && (
        <div className="px-3 pb-3 pl-10 space-y-3">
          {/* Why it matters */}
          <div className="text-sm text-gray-600">
            <span className="font-medium text-gray-700">Why it matters: </span>
            {finding.why_it_matters}
          </div>
          
          {/* Evidence */}
          {finding.evidence && finding.evidence.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {finding.evidence.map((ev, idx) => (
                <span 
                  key={idx}
                  className="text-xs px-2 py-1 bg-white border border-gray-200 rounded text-gray-600"
                >
                  {ev.type === 'count' && <span className="font-semibold">{ev.value}</span>}
                  {ev.type !== 'count' && ev.value}
                </span>
              ))}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            {finding.links?.map((link, idx) => (
              <a
                key={idx}
                href={link.href}
                className="text-xs px-2.5 py-1 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                {link.label}
              </a>
            ))}
            {finding.validator && (
              <button
                onClick={() => onValidatorRun?.(finding.validator)}
                className="text-xs px-2.5 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Run Check
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Main DiagnosticsPanel component
const DiagnosticsPanel = ({ onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  useEffect(() => {
    runDiagnostics();
  }, [tenantId]);
  
  const runDiagnostics = async () => {
    if (!tenantId || !user?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await api.runDiagnostics(tenantId, user.id, 'tenant');
      setResults(res.data);
    } catch (err) {
      console.error('Diagnostics failed:', err);
      setError('Failed to run diagnostics. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleValidatorRun = async (validatorType) => {
    toast.info(`Running ${validatorType} check...`);
    // In a full implementation, this would call the appropriate validator
    // For now, just show a toast
    setTimeout(() => {
      toast.success('Check completed - see results above');
    }, 1000);
  };
  
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-purple-500 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Running diagnostics...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-8 h-8 text-red-400 mb-3" />
        <p className="text-sm text-gray-600 mb-3">{error}</p>
        <button
          onClick={runDiagnostics}
          className="text-sm px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700"
        >
          Try Again
        </button>
      </div>
    );
  }
  
  const { findings, summary, duration_ms, timestamp } = results || {};
  
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold text-gray-900">System Diagnostics</h3>
          </div>
          <button
            onClick={runDiagnostics}
            className="p-1.5 hover:bg-white rounded-lg transition-colors"
            title="Re-run diagnostics"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        
        {/* Summary stats */}
        <div className="flex items-center gap-3">
          {summary?.critical > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <XCircle className="w-3.5 h-3.5 text-red-500" />
              <span className="font-semibold text-red-600">{summary.critical}</span>
              <span className="text-red-500">Critical</span>
            </div>
          )}
          {summary?.high > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="font-semibold text-amber-600">{summary.high}</span>
              <span className="text-amber-500">High</span>
            </div>
          )}
          {summary?.info > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <Info className="w-3.5 h-3.5 text-blue-500" />
              <span className="font-semibold text-blue-600">{summary.info}</span>
              <span className="text-blue-500">Info</span>
            </div>
          )}
          {summary?.total === 0 && (
            <div className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3.5 h-3.5" />
              <span className="font-medium">All checks passed!</span>
            </div>
          )}
        </div>
        
        {/* Timestamp */}
        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          <span>Completed in {duration_ms}ms</span>
        </div>
      </div>
      
      {/* Findings */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {findings?.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h4 className="font-medium text-gray-900 mb-1">Looking good!</h4>
            <p className="text-sm text-gray-500">No issues found. Your system is healthy.</p>
          </div>
        ) : (
          findings?.map((finding, idx) => (
            <FindingCard 
              key={idx} 
              finding={finding} 
              onValidatorRun={handleValidatorRun}
            />
          ))
        )}
      </div>
      
      {/* Footer */}
      <div className="flex-shrink-0 p-3 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 text-center">
          All checks are read-only and safe to run at any time.
        </p>
      </div>
    </div>
  );
};

export default DiagnosticsPanel;
